import { useState } from "react";
import { PenLine, X } from "lucide-react";

// How many whole days have passed since a note's day. Null when there is
// no note at all.
function daysSince(day) {
  if (!day) return null;
  try {
    const d = new Date(day + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((today - d) / 86400000);
  } catch (_e) {
    return null;
  }
}

// The person's local calendar day as YYYY-MM-DD, so the dismiss window
// lines up with the server day (which uses the local date too).
function localToday() {
  const d = new Date();
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10);
}

/**
 * A gentle nudge to jot a note on days the person has gone quiet. Shows
 * only when nothing has been written today (or ever), and never nags:
 * one dismiss quiets it for the rest of the day. The tone stays soft, no
 * streak guilt.
 */
export default function NoteNudge({ lastNoteDay, onWrite }) {
  const todayKey = localToday();
  const dismissedKey = "klareet.notenudge.dismissed";
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" && localStorage.getItem(dismissedKey) === todayKey
  );

  const gap = daysSince(lastNoteDay);

  if (dismissed) return null;
  if (gap !== null && gap <= 0) return null; // already wrote today

  let line;
  if (gap === null) line = "Your journal is empty. One honest sentence is enough to start.";
  else if (gap === 1) line = "Nothing from yesterday yet. Want to catch today in a line?";
  else if (gap <= 6) line = `It has been ${gap} days since your last note. No pressure, a line whenever you are ready.`;
  else line = "It has been a quiet while. Klair kept your spot. A single line is a fine way back in.";

  const dismiss = () => {
    try { localStorage.setItem(dismissedKey, todayKey); } catch (_e) { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div className="k-warm-card p-4 mb-6 flex items-start gap-3 k-fade-in" data-testid="note-nudge">
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: "hsl(var(--klareet-teal) / 0.12)", color: "hsl(var(--klareet-teal))" }}
      >
        <PenLine size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm" style={{ color: "hsl(var(--klareet-ink))" }}>{line}</div>
        {onWrite && (
          <button onClick={onWrite} className="k-btn k-btn-ghost !py-1 !px-2 !text-xs mt-2" data-testid="note-nudge-write">
            <PenLine size={12} /> Jot a note
          </button>
        )}
      </div>
      <button onClick={dismiss} className="k-btn k-btn-ghost !p-1" title="not now" aria-label="dismiss" data-testid="note-nudge-dismiss">
        <X size={14} />
      </button>
    </div>
  );
}
